import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Ruler, Edit, Trash2, Image as ImageIcon, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { ProjectMeasurement, getCustomFieldValue, getCustomFieldUnit } from '../lib/projectMeasurements';
import { getMeasurementPhotos, getMeasurementPhotoUrl, type MeasurementPhoto } from '../services/measurementPhotoService';
import { getMeasurementPresets, MeasurementPreset } from '../lib/measurementPresets';
import { useImageViewer } from './ImageViewerProvider';

interface MeasurementItemProps {
  measurement: ProjectMeasurement;
  onEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
  businessId: string;
}

export default function MeasurementItem({
  measurement,
  onEdit,
  onDelete,
  canEdit,
  businessId
}: MeasurementItemProps) {
  const [photos, setPhotos] = useState<MeasurementPhoto[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const { open: openImageViewer } = useImageViewer();
  const [presets, setPresets] = useState<MeasurementPreset[]>([]);

  useEffect(() => {
    if (measurement.id) {
      loadPhotos();
    }
  }, [measurement.id]);

  useEffect(() => {
    if (businessId) {
      loadPresets();
    }
  }, [businessId]);

  const loadPhotos = async () => {
    if (!measurement.id) return;
    setIsLoadingPhotos(true);
    try {
      const measurementPhotos = await getMeasurementPhotos(measurement.id);
      setPhotos(measurementPhotos);
    } catch (error) {
      console.error('Error loading measurement photos:', error);
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  const loadPresets = async () => {
    try {
      const fetchedPresets = await getMeasurementPresets(businessId);
      setPresets(fetchedPresets);
    } catch (error) {
      console.error('Error loading presets:', error);
    }
  };

  // Helper function to get the unit for a custom field
  const getFieldUnit = (fieldName: string, fieldValue: any): string => {
    // Try the new format first (unit stored with value)
    const unitFromValue = getCustomFieldUnit(fieldValue);
    if (unitFromValue) {
      return unitFromValue;
    }

    // OLD FORMAT: Fall back to looking up the preset
    const matchingPreset = presets.find(preset => 
      preset.name === measurement.category || 
      preset.category === measurement.category
    );

    if (matchingPreset && matchingPreset.custom_fields) {
      const field = matchingPreset.custom_fields.find(f => f.name === fieldName);
      if (field && field.unit) {
        return field.unit;
      }
    }

    // Ultimate fallback to measurement's base unit
    return measurement.unit;
  };

  const formatMeasurement = (value: number | null | undefined, unit: string): string => {
    if (value === null || value === undefined) return '—';
    return `${value} ${unit}`;
  };

  const hasDimensions = measurement.length || measurement.width || measurement.height;
  // Only show area if it exists (it should only exist if it was part of the preset)
  // For existing measurements that might have area incorrectly saved, we'll show it
  // but new measurements will be correct since we only save area if it's in the preset
  const hasArea = measurement.area !== null && measurement.area !== undefined;

  const openPhotoViewer = (index: number) => {
    openImageViewer({
      images: photos.map((p) => ({ src: getMeasurementPhotoUrl(p.file_path), caption: p.file_name, download: p.file_name })),
      index,
    });
  };

  return (
    <div className="px-3 py-2">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Ruler className="h-4 w-4 text-gray-500 flex-shrink-0" />
            <div className="text-[14px] font-semibold text-gray-900" style={{ 
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              letterSpacing: '-0.01em'
            }}>
              {measurement.name}
            </div>
          </div>

          {/* Dimensions */}
          {hasDimensions && (
            <div className="text-[12px] text-gray-600 mb-1.5 space-y-0.5">
              {measurement.length && (
                <div>Length: {formatMeasurement(measurement.length, measurement.unit)}</div>
              )}
              {measurement.width && (
                <div>Width: {formatMeasurement(measurement.width, measurement.unit)}</div>
              )}
              {measurement.height && (
                <div>Height: {formatMeasurement(measurement.height, measurement.unit)}</div>
              )}
            </div>
          )}

          {/* Area */}
          {hasArea && (
            <div className="text-[12px] font-medium text-gray-700 mb-1.5">
              Area: {formatMeasurement(measurement.area, `${measurement.unit}²`)}
            </div>
          )}

          {/* Custom Fields */}
          {measurement.custom_fields && Object.keys(measurement.custom_fields).length > 0 && (
            <div className="text-[12px] text-gray-600 mb-1.5 space-y-0.5">
              {Object.entries(measurement.custom_fields).map(([key, value]) => {
                const displayValue = getCustomFieldValue(value);
                const unit = getFieldUnit(key, value);
                return (
                  <div key={key}>
                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}: {typeof displayValue === 'number' ? `${displayValue} ${unit}` : displayValue}
                  </div>
                );
              })}
            </div>
          )}

          {/* Photos */}
          {photos.length > 0 && (
            <div className="mb-1.5">
              <div className="grid grid-cols-5 gap-1">
                {photos.map((photo, index) => {
                  const photoUrl = getMeasurementPhotoUrl(photo.file_path);
                  return (
                    <div
                      key={photo.id}
                      className="relative aspect-square rounded-md overflow-hidden bg-gray-100 border border-gray-200 cursor-pointer group hover:opacity-90 transition-opacity"
                      onClick={() => openPhotoViewer(index)}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <img
                        src={photoUrl}
                        alt={photo.file_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%" y="50%" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ImageIcon className="h-3 w-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          {measurement.notes && (
            <div className="text-[11px] text-gray-500 italic mb-1.5">
              {measurement.notes}
            </div>
          )}

          {/* Action Buttons */}
          {canEdit && (
            <div className="flex items-center space-x-2 mt-2.5">
              <button
                onClick={onEdit}
                className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-[8px] text-[11px] font-medium hover:bg-blue-100 active:bg-blue-200 active:scale-95 transition-all duration-150 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Edit className="h-3 w-3 inline mr-1" />
                Edit
              </button>
              <button
                onClick={onDelete}
                className="px-3 py-1.5 bg-red-50 text-red-600 rounded-[8px] text-[11px] font-medium hover:bg-red-100 active:bg-red-200 active:scale-95 transition-all duration-150 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Trash2 className="h-3 w-3 inline mr-1" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
